'use client';
import React from 'react';
import type { ReactNode } from 'react';
import type { GlobalDOMAttributes } from '@react-types/shared';
import {
    CheckboxButton as AriaCheckboxButton,
    CheckboxField as AriaCheckboxField,
    type CheckboxFieldProps as AriaCheckboxFieldProps,
} from 'react-aria-components';
import { Field } from '../field';
import { cx } from '../../../utils/cx';

const SEMANTIC_NAME_BASE = 'RomaCheckboxBase';
const SEMANTIC_NAME = 'RomaCheckbox';

type CheckboxVariant = 'primary' | 'secondary' | 'tertiary';

const CHECKBOX_TRANSIENT_ANIMATION_DURATION_MS = 150;
const CHECKBOX_INITIAL_ANIMATION_DELAY_MS = 100;

export interface CheckboxBaseProps {
    size?: 'sm' | 'md';
    variant?: CheckboxVariant;
    className?: string;
    isFocusVisible?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    isIndeterminate?: boolean;
    isInvalid?: boolean;
}

/**
 * Tracks unchecked → checked transitions and returns `true` while the
 * bounce / flash animation classes should be applied.
 *
 * Key behaviours:
 * - Does **not** animate on mount (the ref is initialised to the current
 *   value, so the first effect run never sees a false → true change).
 * - Delays transition styles after mounting without scheduling a separate
 *   render for every checkbox.
 * - Uses React state so the animation classes persist across unrelated
 *   react-aria rerenders (focus, pressed, etc.) that would otherwise
 *   remove transiently-computed classes before the CSS animation finishes.
 * - Clears after the CSS animation has had time to complete, so cloned DOM
 *   does not restart stale transient classes later.
 */
const useCheckboxAnimationState = (
    isChecked: boolean,
): { shouldAnimateCheckedState: boolean; isAnimationEnabled: boolean } => {
    const [shouldAnimate, setShouldAnimate] = React.useState(false);
    const previousCheckedRef = React.useRef(isChecked);
    const initialAnimationEnabledAtRef = React.useRef(performance.now() + CHECKBOX_INITIAL_ANIMATION_DELAY_MS);
    const animationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Avoid animating the checkbox's initial state, including immediately after a paste remount.
    const isAnimationEnabled = performance.now() >= initialAnimationEnabledAtRef.current;

    React.useLayoutEffect(() => {
        if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

        const wasUnchecked = !previousCheckedRef.current;
        previousCheckedRef.current = isChecked;

        if (!isAnimationEnabled) {
            setShouldAnimate(false);
        } else if (wasUnchecked && isChecked) {
            setShouldAnimate(true);
            animationTimeoutRef.current = setTimeout(() => {
                setShouldAnimate(false);
                animationTimeoutRef.current = null;
            }, CHECKBOX_TRANSIENT_ANIMATION_DURATION_MS);
        } else if (!isChecked) {
            setShouldAnimate(false);
        }

        return () => {
            if (!animationTimeoutRef.current) return;

            clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = null;
        };
    }, [isChecked]);

    return { shouldAnimateCheckedState: shouldAnimate, isAnimationEnabled };
};

const styles = {
    common: {
        root: cx(
            // layout
            'relative flex shrink-0 cursor-pointer appearance-none items-center justify-center ring-1 ring-inset',

            // disabled state
            'data-[disabled]:cursor-default data-[disabled]:bg-disabled_subtle data-[disabled]:ring-disabled',
            // invalid state
            'data-[invalid]:ring-error_subtle',
            // focus ring
            'data-[focus-visible]:outline-2 data-[focus-visible]:-outline-offset-1 data-[focus-visible]:outline-focus-ring',
        ),
        icon: {
            base: 'pointer-events-none absolute',
            indeterminate: cx(
                // hidden by default
                'opacity-0',
                // opacity transition
                'transition-opacity duration-100',
                // visible when indeterminate
                'data-[indeterminate]:opacity-100',
            ),
            selected: cx(
                // visible by default
                'opacity-100',
                // hidden when indeterminate
                'data-[indeterminate]:opacity-0',
            ),
        },
    },
    sizes: {
        sm: {
            root: 'rounded-xxs size-4',
            icon: 'size-3',
        },
        md: {
            root: 'rounded-xs size-5',
            icon: 'size-3.5',
        },
    },
    variants: {
        primary: {
            root: cx(
                // default state
                'bg-primary ring-primary',
                // checked state
                'data-[checked]:ring-transparent data-[checked]:bg-fg-primary',
            ),
            // checked flash animation
            checked_flash: 'animate-checkbox-fill-flash_primary',
            icon: cx(
                // default icon color
                'text-fg-primary_alt',
                // disabled icon color
                'data-[disabled]:text-fg-disabled',
            ),
        },
        secondary: {
            root: cx(
                // default state
                'bg-primary ring-primary_focused',
                // checked state
                'data-[checked]:ring-transparent data-[checked]:bg-fg-disabled_subtle',
            ),
            // checked flash animation
            checked_flash: 'animate-checkbox-fill-flash_secondary',
            icon: cx(
                // default icon color
                'text-fg-primary',
                // disabled icon color
                'data-[disabled]:text-fg-disabled',
            ),
        },
        tertiary: {
            root: cx(
                // default state
                'bg-primary ring-primary_focused',
                // checked state
                'data-[checked]:ring-transparent data-[checked]:bg-fg-disabled_subtle',
            ),
            // checked flash animation
            checked_flash: 'animate-checkbox-fill-flash_secondary',
            icon: cx(
                // default icon color
                'text-fg-primary_alt',
                // disabled icon color
                'data-[disabled]:text-fg-disabled',
            ),
        },
    },
};

export const CheckboxBase = ({
    className,
    isSelected,
    isDisabled,
    isIndeterminate,
    isInvalid,
    size = 'sm',
    variant = 'primary',
    isFocusVisible = false,
}: CheckboxBaseProps) => {
    const isChecked = Boolean(isSelected || isIndeterminate);
    const { shouldAnimateCheckedState, isAnimationEnabled } = useCheckboxAnimationState(isChecked);

    return (
        <div className={cx(SEMANTIC_NAME_BASE, shouldAnimateCheckedState && 'animate-checkbox-bounce')}>
            {/* 👆 wrapper div provides target for bounce animation */}
            <div
                data-size={size}
                data-variant={variant}
                data-checked={isChecked || undefined}
                data-selected={isSelected || undefined}
                data-indeterminate={isIndeterminate || undefined}
                data-disabled={isDisabled || undefined}
                data-invalid={isInvalid || undefined}
                data-focus-visible={isFocusVisible || undefined}
                className={cx(
                    styles.common.root,
                    styles.sizes[size].root,
                    styles.variants[variant].root,
                    !isAnimationEnabled
                        ? 'transition-none'
                        : shouldAnimateCheckedState
                          ? 'transition-[box-shadow] duration-100 ease-linear'
                          : 'transition-[background-color,box-shadow] duration-100 ease-linear',
                    shouldAnimateCheckedState && styles.variants[variant].checked_flash,
                    isAnimationEnabled && !isChecked && 'delay-[50ms]',
                    className,
                )}
            >
                {/* Indeterminate line - no stroke animation */}
                <svg
                    aria-hidden="true"
                    viewBox="0 0 14 14"
                    fill="none"
                    data-indeterminate={isIndeterminate || undefined}
                    data-disabled={isDisabled || undefined}
                    className={cx(
                        styles.common.icon.base,
                        styles.common.icon.indeterminate,
                        styles.sizes[size].icon,
                        styles.variants[variant].icon,
                    )}
                >
                    <path
                        d="M2.91675 7H11.0834"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>

                {/* Checkmark tick - with stroke animation */}
                <svg
                    aria-hidden="true"
                    viewBox="0 0 14 14"
                    fill="none"
                    data-indeterminate={isIndeterminate || undefined}
                    data-disabled={isDisabled || undefined}
                    className={cx(
                        styles.common.icon.base,
                        styles.common.icon.selected,
                        styles.sizes[size].icon,
                        styles.variants[variant].icon,
                    )}
                >
                    <path
                        d="M2.33325 7L5.24992 9.91667L11.6666 3.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="[stroke-dasharray:16]"
                        style={{
                            strokeDashoffset: isSelected ? 0 : 16,
                            transition: !isAnimationEnabled
                                ? 'none'
                                : isSelected
                                  ? 'stroke-dashoffset 150ms linear 150ms'
                                  : 'stroke-dashoffset 100ms linear',
                        }}
                    />
                </svg>
            </div>
        </div>
    );
};
CheckboxBase.displayName = SEMANTIC_NAME_BASE;

export type CheckboxProps = Omit<
    AriaCheckboxFieldProps,
    'children' | 'className' | 'style' | 'render' | keyof GlobalDOMAttributes
> & {
    size?: 'sm' | 'md';
    variant?: CheckboxVariant;
    label?: ReactNode;
    hint?: ReactNode;
    /** Applied to the clickable label row. */
    className?: string;
};

const CheckboxRoot: React.FC<CheckboxProps> = ({
    label,
    hint,
    size = 'sm',
    variant = 'primary',
    className,
    ...fieldProps
}) => {
    const sizes = {
        sm: {
            field: 'gap-0',
            button: 'gap-2',
            label: 'text-sm',
            hint: 'text-sm',
            hintIndent: 'pl-6',
            checkbox: label || hint ? 'mt-0.5' : '',
        },
        md: {
            field: 'gap-0.5',
            button: 'gap-3',
            label: 'text-md',
            hint: 'text-md',
            hintIndent: 'pl-8',
            checkbox: '',
        },
    };

    return (
        <Field as={AriaCheckboxField} {...fieldProps} className={cx('h-auto w-auto', sizes[size].field)}>
            <AriaCheckboxButton
                className={({ isDisabled, isInvalid }) =>
                    cx(
                        SEMANTIC_NAME,
                        'flex items-start select-none font-medium',
                        isDisabled && 'cursor-default',
                        isInvalid ? 'text-error-primary' : 'text-secondary',
                        sizes[size].button,
                        sizes[size].label,
                        className,
                    )
                }
                data-test-id={`checkbox-${fieldProps.name || 'unnamed'}`}
            >
                {({ isSelected, isIndeterminate, isDisabled, isFocusVisible, isInvalid }) => (
                    <>
                        <CheckboxBase
                            size={size}
                            variant={variant}
                            isSelected={isSelected}
                            isIndeterminate={isIndeterminate}
                            isDisabled={isDisabled}
                            isFocusVisible={isFocusVisible}
                            isInvalid={isInvalid}
                            className={sizes[size].checkbox}
                        />
                        {label &&
                            (typeof label === 'string' || typeof label === 'number' ? (
                                <Field.LabelText size={size} className={isInvalid ? 'text-error-primary' : undefined}>
                                    {label}
                                </Field.LabelText>
                            ) : (
                                label
                            ))}
                    </>
                )}
            </AriaCheckboxButton>
            {hint && <Field.HintText className={cx(sizes[size].hint, sizes[size].hintIndent)}>{hint}</Field.HintText>}
        </Field>
    );
};
CheckboxRoot.displayName = SEMANTIC_NAME;

export const Checkbox = Object.assign(CheckboxRoot, {
    Base: CheckboxBase,
});
